<i18n locale="en">
{
  "done": "Done",
  "labels": {
    "button": "Button",
    "button-title": "Button Title",
    "description": "Description",
    "image": "Photo",
    "link": "Link",
    "subtitle": "Subtitle",
    "title": "Title"
  },
  "placeholders": {
    "description": "Aa",
    "subtitle": "Aa",
    "title": "Aa"
  }
}
</i18n>

<template>
  <EditorLayout>
    <div class="flex-col gap-8">
      <OwnInputContainer
        :label="t('labels.image')"
        :errors="image.status.errors"
      >
        <ImageInput
          v-model="image.value"
          :label="t('labels.image')"
          :crop-width="1000"
        />
      </OwnInputContainer>
      <OwnInputContainer
        :label="t('labels.title')"
        :errors="title.status.errors"
        :max-chars="websiteSectionMaxChars(MAX_TITLE_LENGTH)"
        :value="title.value"
      >
        <OwnInput
          v-model="title.value"
          :placeholder="t('placeholders.title')"
        />
      </OwnInputContainer>
      <OwnInputContainer
        :label="t('labels.subtitle')"
        :errors="subtitle.status.errors"
        :max-chars="websiteSectionMaxChars(MAX_SUBTITLE_LENGTH)"
        :value="subtitle.value"
      >
        <OwnInput
          v-model="subtitle.value"
          :placeholder="t('placeholders.subtitle')"
        />
      </OwnInputContainer>
      <OwnInputContainer
        :label="t('labels.description')"
        :errors="description.status.errors"
        :max-chars="websiteSectionMaxChars(MAX_DESCRIPTION_LENGTH)"
        :value="description.value"
      >
        <OwnTextarea
          v-model="description.value"
          :placeholder="t('placeholders.description')"
        />
      </OwnInputContainer>

      <ToggleCard
        :label="t('labels.link')"
        :model-value="Boolean(localLink)"
        @update:model-value="toggleLink"
      >
        <div v-if="localLink" class="flex-col gap-6">
          <OwnInputContainer
            :label="t('labels.button-title')"
            :errors="buttonLabel.status.errors"
          >
            <OwnInput v-model="buttonLabel.value" />
          </OwnInputContainer>
          <CardLinkEditor
            class="cards-editor__action-inputs"
            :model-value="localLink"
            @update="onLinkUpdate"
          />
        </div>
      </ToggleCard>
    </div>

    <template #actions>
      <OwnButton
        :disabled="!isFormValid"
        :text="t('done')"
        variant="primary"
        @click="$emit('back')"
      />
    </template>
  </EditorLayout>
</template>

<script>
import { useI18n } from 'vue-i18n'

import ToggleCard from '@/components/cards/ToggleCard.vue'
import { ImageInput } from '@/components/inputs/ImageInput'
import {
  websiteSectionMaxChars,
  WebsiteSectionValidators,
} from '@/features/website-builder/builder/validation/websiteSectionValidators'
import { FormBuilder } from '@/forms'
import EditorLayout from '@/lib/builder/public/editor/EditorLayout.vue'
import { OwnButton } from '@/ui/OwnButton'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnTextarea } from '@/ui/OwnTextarea'

import CardLinkEditor from './CardLinkEditor.vue'

const MAX_DESCRIPTION_LENGTH = 200
const MAX_SUBTITLE_LENGTH = 120
const MAX_TITLE_LENGTH = 50

export default {
  name: 'CardDetailEditor',
  components: {
    CardLinkEditor,
    EditorLayout,
    ImageInput,
    OwnButton,
    OwnInput,
    OwnInputContainer,
    OwnTextarea,
    ToggleCard,
  },
  mixins: [
    FormBuilder({
      buttonLabel: {
        debounce: 250,
        validateOnInit: false,
        validators: [WebsiteSectionValidators.maxLength(50)],
      },
      description: {
        debounce: 250,
        validateOnInit: true,
        validators: [
          WebsiteSectionValidators.maxLength(MAX_DESCRIPTION_LENGTH),
        ],
      },
      image: {
        debounce: 250,
        validateOnInit: false,
        validators: [],
      },
      subtitle: {
        debounce: 250,
        validateOnInit: false,
        validators: [WebsiteSectionValidators.maxLength(MAX_SUBTITLE_LENGTH)],
      },
      title: {
        debounce: 250,
        validateOnInit: true,
        validators: [
          WebsiteSectionValidators.required,
          WebsiteSectionValidators.maxLength(MAX_TITLE_LENGTH),
        ],
      },
    }),
  ],
  props: {
    item: { required: true, type: Object },
  },
  emits: ['update', 'back'],
  setup() {
    const { t } = useI18n()

    return { t, websiteSectionMaxChars }
  },
  data() {
    return {
      MAX_DESCRIPTION_LENGTH,
      MAX_SUBTITLE_LENGTH,
      MAX_TITLE_LENGTH,
      localLink: this.item.link ?? null,
    }
  },
  created() {
    const { item } = this

    this.setInitialFormValues({
      buttonLabel: item.buttonLabel,
      description: item.description,
      image: item.image,
      subtitle: item.subtitle,
      title: item.title,
    })
  },
  methods: {
    createDefaultLink() {
      return {
        params: {
          label: '',
          url: '',
        },
        type: 'external',
      }
    },
    emitUpdate(formValues = this.formValues) {
      const updated = {
        buttonLabel: formValues.buttonLabel ?? null,
        description: formValues.description ?? null,
        image: formValues.image ?? null,
        link: this.localLink ?? null,
        subtitle: formValues.subtitle ?? null,
        title: formValues.title,
      }

      this.$emit('update', updated)
    },
    onFormValuesUpdate(formValues) {
      this.emitUpdate(formValues)
    },
    onLinkUpdate(newLink) {
      this.localLink = newLink
      this.emitUpdate()
    },
    toggleLink(enabled) {
      if (enabled) {
        this.localLink = this.createDefaultLink()
        this.emitUpdate()
        return
      }

      this.localLink = null
      this.emitUpdate()
    },
  },
}
</script>

<style lang="scss" scoped>
.cards-editor__action-inputs {
  padding-top: 8px;
}
</style>
